import React from "react";
import { Loader2 } from "lucide-react";

function Line({ n, children, warn }) {
  const tone = n ? (warn ? "text-rose-300" : "text-amber-300") : "text-zinc-600";
  return <li className={tone}><span className="mono inline-block w-12 text-right mr-2 tabular-nums">{n}</span>{children}</li>;
}

export function RetentionPlan({ plan, busy, onRun, onCancel }) {
  const nothing = !plan.sites_to_update && !plan.would_archive && !plan.would_purge && !plan.would_delete_tombstones;
  return (
    <div data-testid="retention-plan" className="border border-zinc-800 rounded-sm p-3 space-y-2 text-xs">
      <div className="text-zinc-200">Every site → <span className="mono text-amber-300">{plan.hot_days}d live → {plan.archive_days}d archived (noindex) → 410 Gone for {plan.tombstone_ttl_days}d → deleted</span></div>
      <ul className="space-y-1">
        <Line n={plan.sites_to_update}>of {plan.sites_total} site(s) move to this window (old sites included)</Line>
        <Line n={plan.would_archive}>published article(s) older than {plan.hot_days}d → archived now</Line>
        <Line n={plan.would_purge} warn>article(s) past {plan.hot_days + plan.archive_days}d → purged now (R2 images deleted, edge cache cleared, URL answers 410)</Line>
        <Line n={plan.would_delete_tombstones}>410 tombstone(s) older than {plan.tombstone_ttl_days}d → hard-deleted</Line>
      </ul>
      <div className="text-zinc-500">The daily 03:00 UTC job keeps enforcing the window afterwards. Evergreen articles are never archived.</div>
      <div className="flex gap-2">
        <button type="button" data-testid="retention-run-btn" disabled={busy || nothing} onClick={onRun} className="px-3 py-1.5 text-xs font-semibold bg-rose-500 text-zinc-950 hover:bg-rose-400 disabled:opacity-40 rounded-sm">
          {nothing ? "Nothing to change" : "Apply to all sites & run now"}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-xs border border-zinc-800 rounded-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
      </div>
    </div>
  );
}

const STAGE_TEXT = {
  queued: "Queued — waiting for the background worker to pick it up…",
  lifecycle: "Running lifecycle in the background worker (archive → purge → tombstones)…",
};

export function RetentionJob({ job }) {
  const r = job.result || {};
  if (job.status === "queued" || job.status === "running") {
    const waited = job.started_at ? (Date.now() - new Date(job.started_at).getTime()) / 1000 : 0;
    return (
      <div data-testid="retention-job" className="text-xs text-amber-300 mono flex items-center gap-2 flex-wrap">
        <Loader2 size={12} className="animate-spin" /> {STAGE_TEXT[job.stage] || STAGE_TEXT.lifecycle}
        {job.status === "queued" && waited > 60 && <span className="text-rose-300">still queued after {Math.round(waited)}s — is the scheduler worker running?</span>}
      </div>
    );
  }
  if (job.status === "failed") return <div data-testid="retention-job" className="text-xs text-red-400 mono">Failed: {job.error}</div>;
  return (
    <div data-testid="retention-job" className="text-xs text-emerald-300 border border-emerald-900/60 bg-emerald-950/30 px-3 py-2 rounded-sm mono">
      Done · {r.sites_updated} site(s) updated{"archived" in r ? ` · archived ${r.archived} · purged ${r.purged} (${r.r2_objects_deleted} R2 objects) · ${r.tombstones_deleted} tombstone(s) deleted` : ""}
    </div>
  );
}
